import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Mail, Send, Loader2, Check, Eye, CalendarClock, ShieldAlert, Play, BookOpen } from "lucide-react";
import { toast } from "sonner";

export default function EmailNotificationsCard() {
  const [prefs, setPrefs] = useState(null);
  const [sending, setSending] = useState(false);
  const [weeklyBusy, setWeeklyBusy] = useState(false);
  const [weeklyPreview, setWeeklyPreview] = useState(null);
  const [reputation, setReputation] = useState(null);
  const [resumeBusy, setResumeBusy] = useState(false);
  const [hubBusy, setHubBusy] = useState(false);
  const [hubPreview, setHubPreview] = useState(null);

  useEffect(() => {
    api.get("/notifications/email-prefs")
      .then((r) => setPrefs(r.data))
      .catch(() => toast.error("Couldn't load preferences"));
    api.get("/buyer-nudges/reputation")
      .then((r) => setReputation(r.data))
      .catch(() => {}); // non-fatal
  }, []);

  const update = async (patch) => {
    const prev = prefs;
    const next = { ...prefs, ...patch };
    setPrefs(next);
    try {
      const { data } = await api.put("/notifications/email-prefs", patch);
      setPrefs(data);
    } catch (err) {
      // Roll back optimistic update on failure.
      setPrefs(prev);
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const sendTest = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/notifications/email-prefs/test");
      if (data.sent) {
        toast.success("Test email sent — check your inbox.");
      } else {
        toast.error("Email could not be sent. See email log for details.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const loadWeeklyPreview = async () => {
    setWeeklyBusy(true);
    try {
      const { data } = await api.get("/notifications/weekly-engagement/preview");
      setWeeklyPreview(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't load preview");
    } finally {
      setWeeklyBusy(false);
    }
  };

  const sendWeeklyNow = async () => {
    setWeeklyBusy(true);
    try {
      const { data } = await api.post("/notifications/weekly-engagement/send-now");
      if (data.sent) {
        toast.success("Weekly recap sent — check your inbox.");
      } else {
        toast.error("Email could not be sent. See email log for details.");
      }
      setWeeklyPreview(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    } finally {
      setWeeklyBusy(false);
    }
  };

  const loadHubPreview = async () => {
    setHubBusy(true);
    try {
      const { data } = await api.get("/notifications/resources-hub/preview");
      setHubPreview(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't load preview");
    } finally {
      setHubBusy(false);
    }
  };

  const sendHubNow = async () => {
    setHubBusy(true);
    try {
      const { data } = await api.post("/notifications/resources-hub/send-now");
      setHubPreview({ resources: data.resources });
      toast.success(data.sent ? "Resources Hub email sent to your inbox." : "Email logged but delivery skipped (check Resend domain/limits).");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    } finally {
      setHubBusy(false);
    }
  };


  const resumeNudges = async () => {
    setResumeBusy(true);
    try {
      const { data } = await api.post("/buyer-nudges/resume");
      if (data.resumed) {
        toast.success("Auto-nudges resumed.");
        // Refresh both prefs + reputation
        const [p, r] = await Promise.all([
          api.get("/notifications/email-prefs"),
          api.get("/buyer-nudges/reputation"),
        ]);
        setPrefs(p.data);
        setReputation(r.data);
      } else {
        toast.error("Nothing to resume.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Resume failed");
    } finally {
      setResumeBusy(false);
    }
  };

  if (!prefs) {
    return (
      <div className="card" data-testid="settings-email-card">
        <div className="label-eyebrow flex items-center gap-1.5 mb-3"><Mail size={11} /> Email notifications</div>
        <div className="text-sm text-[var(--ink-3)] flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>
      </div>
    );
  }

  const Row = ({ label, hint, k }) => (
    <label className="flex items-start justify-between gap-3 py-2 cursor-pointer" data-testid={`email-pref-${k}-row`}>
      <div className="min-w-0">
        <div className="text-sm font-semibold">{label}</div>
        <div className="text-xs text-[var(--ink-3)] mt-0.5">{hint}</div>
      </div>
      <input
        type="checkbox"
        className="mt-1 w-4 h-4 accent-[var(--primary)] cursor-pointer"
        checked={!!prefs[k]}
        onChange={(e) => update({ [k]: e.target.checked })}
        data-testid={`email-pref-${k}-toggle`}
      />
    </label>
  );

  const Stat = ({ label, value, highlight }) => (
    <div className={"rounded-md border px-2 py-1.5 " + (highlight ? "bg-[var(--primary)] border-transparent text-white" : "bg-white border-[var(--line)]")}>
      <div className={"text-[9px] uppercase tracking-wider " + (highlight ? "text-white/70" : "text-[var(--ink-3)]")}>{label}</div>
      <div className={"font-semibold text-sm " + (highlight ? "text-white" : "text-[var(--ink)]")}>{value}</div>
    </div>
  );

  return (
    <div className="card" data-testid="settings-email-card">
      <div className="label-eyebrow flex items-center gap-1.5 mb-3"><Mail size={11} /> Email notifications</div>
      <p className="text-xs text-[var(--ink-3)] mb-3">
        We&rsquo;ll only email you about deals you&rsquo;ve opted into. You can change this any time.
      </p>

      {reputation?.paused && (
        <div
          className="mb-3 rounded-lg border border-amber-300 bg-amber-50 p-3"
          data-testid="reputation-paused-banner"
        >
          <div className="flex items-start gap-2.5">
            <ShieldAlert size={16} className="text-amber-600 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-amber-900">
                Auto-nudges paused to protect your sender reputation
              </div>
              <div className="text-xs text-amber-800 mt-1 leading-relaxed">
                Last {reputation.thresholds.window_days}d: <strong>{reputation.paused_stats?.sent ?? 0}</strong> sent ·
                only <strong>{reputation.paused_stats?.reply_rate ?? 0}%</strong> reply rate.
                Warm the list with a few hand-picked sends, then resume.
              </div>
              <button
                type="button"
                onClick={resumeNudges}
                disabled={resumeBusy}
                className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold bg-amber-600 text-white px-3 py-1.5 rounded-md hover:bg-amber-700 disabled:opacity-60"
                data-testid="reputation-resume-btn"
              >
                {resumeBusy ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />} Resume auto-nudges
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="divide-y divide-[var(--line)]">
        <Row k="instant_go" label="Instant GO alert" hint="Email me the moment a new GO deal hits one of my saved searches." />
        <Row k="instant_maybe" label="Instant MAYBE alert" hint="Also email me for MAYBE-verdict listings (borderline deals)." />
        <Row k="daily_digest" label="Daily morning digest" hint="A single 7am email summarising the top 3 deals from the last 24h." />
        <Row k="buyer_nudges" label="Auto-nudge engaged buyers" hint="When a buyer opens photos on a shared report but doesn't reply, email them a friendly follow-up within 48 hours. Max 20/day per account, 14-day cooldown per buyer-deal pair. Sender: your address." />
        <Row k="weekly_engagement" label="Monday engagement recap" hint="Every Monday morning — a recap of the 3 hottest buyers and most-viewed deals from the past 7 days. Helps you spot which buyer to call first." />
        <Row k="streak_saver" label="Streak-saver nudge" hint="Afternoon email when your closing streak is about to break — sent only when you closed yesterday but haven't yet today and the streak is ≥2 days." />
        <Row k="resources_hub" label="Thursday Resources Hub" hint="A curated roundup of 3 wholesaler resources (new + most-viewed) emailed every Thursday morning — scripts, checklists, contracts, calculators. Already free in-app, this is a friendly nudge to keep them top of mind." />
      </div>

      {/* Weekly engagement preview block */}
      <div className="mt-4 pt-3 border-t border-[var(--line)]" data-testid="weekly-engagement-block">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-semibold flex items-center gap-1.5 text-[var(--ink-2)]">
            <CalendarClock size={12} /> Weekly engagement recap
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="btn-outline text-xs"
              onClick={loadWeeklyPreview}
              disabled={weeklyBusy}
              data-testid="weekly-engagement-preview-btn"
            >
              {weeklyBusy ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} />} Preview
            </button>
            <button
              type="button"
              className="btn-outline text-xs"
              onClick={sendWeeklyNow}
              disabled={weeklyBusy}
              data-testid="weekly-engagement-send-btn"
            >
              {weeklyBusy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Send to me now
            </button>
          </div>
        </div>
        {weeklyPreview && (
          <div className="rounded-lg bg-[var(--bg-2)] border border-[var(--line)] p-3 text-xs" data-testid="weekly-engagement-preview-data">
            <div className="text-[10px] uppercase tracking-wider text-[var(--ink-3)] mb-1.5">
              Window · {weeklyPreview.week_label}
            </div>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <Stat label="Views" value={weeklyPreview.totals?.views ?? 0} />
              <Stat label="Buyers" value={weeklyPreview.totals?.unique_buyers ?? 0} />
              <Stat label="Deals" value={weeklyPreview.totals?.unique_leads ?? 0} />
            </div>
            {(weeklyPreview.totals?.nudges_sent ?? 0) > 0 && (
              <div className="grid grid-cols-3 gap-2 mb-3" data-testid="weekly-engagement-reply-rate">
                <Stat label="Nudges" value={weeklyPreview.totals.nudges_sent} />
                <Stat label="Reply rate" value={`${weeklyPreview.totals.reply_rate}%`} highlight />
                <Stat label="YES of replies" value={`${weeklyPreview.totals.yes_rate}%`} />
              </div>
            )}
            <div className="text-[10px] uppercase tracking-wider text-[var(--ink-3)] mb-1">🔥 Top buyers</div>
            {(weeklyPreview.top_buyers || []).length === 0
              ? <div className="text-[var(--ink-3)] mb-2">No identified buyer engagement this week.</div>
              : (
                <ul className="space-y-1.5 mb-2">
                  {weeklyPreview.top_buyers.map((b) => (
                    <li key={b.team_member_id} className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-medium truncate">{b.name}</div>
                        <div className="text-[10px] text-[var(--ink-3)]">{b.views} views · score {b.score}</div>
                      </div>
                      {b.nudge_token && (
                        <a
                          href={`/api/nudge-click/${b.nudge_token}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="shrink-0 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-[var(--primary)] text-white px-2 py-1 rounded-md hover:opacity-90"
                          data-testid={`weekly-engagement-nudge-cta-${b.team_member_id}`}
                        >
                          Send nudge →
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            <div className="text-[10px] uppercase tracking-wider text-[var(--ink-3)] mb-1">📈 Top deals</div>
            {(weeklyPreview.top_deals || []).length === 0
              ? <div className="text-[var(--ink-3)]">No photo views logged this week.</div>
              : (
                <ul className="space-y-1">
                  {weeklyPreview.top_deals.map((d) => (
                    <li key={d.lead_id} className="flex items-center justify-between">
                      <span className="font-medium truncate mr-2">{d.address}</span>
                      <span className="text-[var(--ink-3)] shrink-0">{d.views} views · {d.unique_buyers} buyer{d.unique_buyers === 1 ? "" : "s"}</span>
                    </li>
                  ))}
                </ul>
              )}
          </div>
        )}
      </div>

      {/* Resources Hub preview block */}
      <div className="mt-4 pt-3 border-t border-[var(--line)]" data-testid="resources-hub-block">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-semibold flex items-center gap-1.5 text-[var(--ink-2)]">
            <BookOpen size={12} /> Thursday Resources Hub
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="btn-outline text-xs"
              onClick={loadHubPreview}
              disabled={hubBusy}
              data-testid="resources-hub-preview-btn"
            >
              {hubBusy ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} />} Preview
            </button>
            <button
              type="button"
              className="btn-outline text-xs"
              onClick={sendHubNow}
              disabled={hubBusy}
              data-testid="resources-hub-send-btn"
            >
              {hubBusy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Send to me now
            </button>
          </div>
        </div>
        {hubPreview && (
          <div className="rounded-lg bg-[var(--bg-2)] border border-[var(--line)] p-3 text-xs" data-testid="resources-hub-preview-data">
            <div className="text-[10px] uppercase tracking-wider text-[var(--ink-3)] mb-2">
              Next Thursday&apos;s roundup · {hubPreview.resources?.length || 0} resources
            </div>
            {(hubPreview.resources || []).length === 0 ? (
              <div className="text-[var(--ink-3)]">No free resources are currently published.</div>
            ) : (
              <ul className="space-y-1.5">
                {hubPreview.resources.map((r) => (
                  <li key={r.id} className="flex items-center justify-between gap-2" data-testid="resources-hub-preview-row">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{r.title}</div>
                      <div className="text-[10px] uppercase tracking-wider text-[var(--ink-3)]">{r.category} · {r.view_count || 0} views</div>
                    </div>
                    <a
                      href={`/app/resources/${r.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="shrink-0 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-[var(--primary)] text-white px-2 py-1 rounded-md hover:opacity-90"
                    >
                      Open →
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between mt-4 pt-3 border-t border-[var(--line)]">
        <div className="text-xs text-[var(--ink-3)] flex items-center gap-1">
          <Check size={12} className="text-emerald-600" /> Auto-saved
        </div>
        <button
          type="button"
          className="btn-outline text-sm"
          onClick={sendTest}
          disabled={sending}
          data-testid="email-send-test-btn"
        >
          {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Send test to my inbox
        </button>
      </div>
    </div>
  );
}
